import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth, useWallet } from "@crossmint/client-sdk-react-ui";
import { Layout } from "./components/Layout";
import { Dashboard } from "./components/Dashboard";
import { WalletPage } from "./components/WalletPage";
import { AddFundsPage } from "./components/AddFundsPage";
import { TransferPage } from "./components/TransferPage";
import AnalyticsPage from "./components/AnalyticsPage";
import { FAQPage } from "./components/FAQPage";
import SupportPage from "./components/SupportPage";
import Login from "./components/Login";
import LandingPage from "./components/LandingPage";
import { useDispatch } from "react-redux";
import { setUser } from "./slices/userSlices";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import PaymentRequestPage from "./components/PaymentRequestPage";
import { GridLoader } from "react-spinners";
import Comingsoon from "./components/Comingsoon";
import EarnPage from "./components/EarnPage";
import FeatureUnavailable from "./components/FeatureUnavailable";
import HistoryPage from "./components/HistoryPage";
import CardSoon from "./components/CardSoon";
import ExtentionInfo from "./components/ExtentionInfo";
import CollectInfo from "./components/Collect";
import PublicPaymentPage from "./components/PublicPaymentPage";
import PublicInvoicePage from "./components/PublicInvoicePage";
import UniversalProfilePage from "./components/UniversalProfilePage";
import LightningPaymentPage from "./components/LightningPaymentPage";
import PyusdPaymentPage from "./components/PyusdPaymentPage";
import PaymentInvoicePage from "./components/PaymentInvoicePage";
import CashappPayPage from "./components/CashappPayPage";
import ApiSettingsPage from "./components/developers/ApiSettingsPage";
import DocumentationPage from "./components/developers/DocumentationPage";
import WebhooksPage from "./components/developers/webhooks/WebhooksPage";
import EndpointDetailPage from "./components/developers/webhooks/EndpointDetailPage";
import DeliveryDetailPage from "./components/developers/webhooks/DeliveryDetailPage";
import AmlPolicyPage from "./components/AmlPolicyPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import TermsOfUsePage from "./components/TermsOfUsePage";
import PreLoader from "./components/PreLoader";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { status: authStatus } = useAuth();
  const loc = useLocation();

  // Only check auth status - wallet can load separately after navigation
  // This prevents redirect loops when wallet is still loading
  if (authStatus === "logged-in") {
    return <>{children}</>;
  }

  // Remember a deep developer link (e.g. a webhook alert email's delivery
  // page) so Login can send the user back there instead of Home.
  if (loc.pathname.startsWith("/developers/")) {
    try {
      sessionStorage.setItem("cashtap:returnTo", loc.pathname + loc.search);
    } catch {
      /* storage unavailable: Login falls back to its default destination */
    }
  }

  // If not logged in, redirect to login
  return <Navigate to="/login" replace />;
};

// Component to handle smooth scroll on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

function App() {
  const { user, status: authStatus } = useAuth();
  const { wallet } = useWallet();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [preloaderDone, setPreloaderDone] = useState(false);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      // Only check auth status - wallet can load separately
      if (authStatus === "logged-in") {
        const userId = user?.email || wallet?.address?.toLowerCase();
        if (!userId) {
          setIsLoading(false);
          return;
        }

        try {
          const userRef = doc(db, "users", userId);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            dispatch(
              setUser({
                uid: userId,
                email: user?.email || null,
                wallet: wallet?.address?.toLowerCase() || null,
                smartWallet: wallet?.address?.toLowerCase() || null, // Crossmint wallet address
                username: userData.username,
                joinedAt: userData.joinDate?.toDate?.().toISOString() || null,
                phone: null,
                isBusiness: userData.isBusiness || false,
              } as any)
            );
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [authStatus, user, wallet, dispatch]);

  // Show preloader on first visit; afterwards fall back to the old spinner if
  // auth data is still resolving after the preloader has already exited.
  if (!preloaderDone) {
    return <PreLoader onComplete={() => setPreloaderDone(true)} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]">
        <GridLoader color="#61CE70" size={20} />
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public marketing landing page — now the root. The exact "/" route
            outranks the "/*" Layout splat, so the app shell (and its
            index → dashboard redirect) is untouched for every other path.
            /landingPage stays as an alias so previously shared links work. */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landingPage" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />

        {/* Public payment pages (no auth required) */}
        <Route path="/paymentRequest/buymeCoffee/:walletAddress" element={<PublicPaymentPage type="buymeCoffee" />} />
        <Route path="/paymentRequest/donation/:walletAddress" element={<PublicPaymentPage type="donation" />} />
        <Route path="/paymentRequest/:walletAddress" element={<PublicPaymentPage type="request" />} />
        <Route path="/pay/:slug" element={<UniversalProfilePage />} />
        <Route path="/pay/:slug/payment" element={<PublicPaymentPage type="request" useSlug />} />
        <Route path="/pay/:slug/coffee" element={<PublicPaymentPage type="buymeCoffee" useSlug />} />
        <Route path="/pay/:slug/donate" element={<PublicPaymentPage type="donation" useSlug />} />
        <Route path="/invoice/:invoiceId" element={<PublicInvoicePage />} />
        <Route path="/pay/:slug/lightning" element={<LightningPaymentPage />} />
        <Route path="/lightning" element={<LightningPaymentPage />} />
        <Route path="/lightning/:orderId" element={<LightningPaymentPage />} />
        <Route path="/payment-invoice/:invoiceId" element={<PaymentInvoicePage />} />
        <Route path="/cashapppay/:slug" element={<CashappPayPage />} />

        {/* Public legal pages — no auth required, indexable */}
        <Route path="/aml-policy" element={<AmlPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />

        {/* Public developer documentation. Lives outside the Layout
            group on purpose so it's reachable without an account — the
            whole point is letting prospective integrators read the
            spec before signing up. The page is self-contained (its
            own PageHeader, sticky TOC, "back to top" affordance), so
            it doesn't need the in-app shell to render correctly. */}
        <Route path="/developers/docs" element={<DocumentationPage />} />

        <Route path="/*" element={<Layout />}>
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="collect"
            element={
              <ProtectedRoute>
                <CollectInfo />
              </ProtectedRoute>
            }
          />
          <Route
            path="wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="earn"
            element={
              <ProtectedRoute>
                <EarnPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="card-soon"
            element={
              <ProtectedRoute>
                <CardSoon />
              </ProtectedRoute>
            }
          />
          <Route
            path="shopping"
            element={
              <ProtectedRoute>
                <Comingsoon />
              </ProtectedRoute>
            }
          />
          <Route
            path="add-funds"
            element={
              <ProtectedRoute>
                <AddFundsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="add-funds/lightning"
            element={<LightningPaymentPage />}
          />
          <Route
            path="add-funds/lightning/:orderId"
            element={<LightningPaymentPage />}
          />
          {/* PYUSD (PayPal / Venmo) add-funds. Single page handles both
              "paypal" and "venmo" sources; the source is passed via
              navigation state + ?source= query so the page renders the
              correct branded copy. Beta-gated at the tile level + on
              the backend. */}
          <Route
            path="add-funds/pyusd"
            element={<PyusdPaymentPage />}
          />
          <Route
            path="add-funds/pyusd/:exchangeId"
            element={<PyusdPaymentPage />}
          />
          <Route
            path="extension-info"
            element={<ExtentionInfo />}
          />
          <Route
            path="history"
            element={
              <ProtectedRoute>
                <HistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="transfer"
            element={
              <ProtectedRoute>
                <TransferPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          {/* WIND-DOWN: Bridge onramp/offramp pages are disabled. Restore the
              BridgeOnRamp / BridgeOffRamp imports and elements to re-enable. */}
          <Route
            path="setup-usbank"
            element={
              <ProtectedRoute>
                <FeatureUnavailable title="U.S. Bank Account Setup" />
              </ProtectedRoute>
            }
          />
          <Route
            path="link-bank"
            element={
              <ProtectedRoute>
                <FeatureUnavailable title="Bank Withdrawals" />
              </ProtectedRoute>
            }
          />
          <Route
            path="paymentRequests"
            element={
              <ProtectedRoute>
                <PaymentRequestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="faqs"
            element={
              <ProtectedRoute>
                <FAQPage />
              </ProtectedRoute>
            }
          />
          {/* Developers section — split into API Settings + Documentation.
              The bare /developers path redirects to API Settings so existing
              links keep working. Both pages share the same protected wrapper. */}
          <Route
            path="developers"
            element={<Navigate to="/developers/api-settings" replace />}
          />
          <Route
            path="developers/api-settings"
            element={
              <ProtectedRoute>
                <ApiSettingsPage />
              </ProtectedRoute>
            }
          />
          {/* Webhooks — endpoint management + delivery history. The two
              detail pages are the deep-link targets of the alert emails. */}
          <Route
            path="developers/webhooks"
            element={
              <ProtectedRoute>
                <WebhooksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="developers/webhooks/endpoints/:endpointId"
            element={
              <ProtectedRoute>
                <EndpointDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="developers/webhooks/deliveries/:deliveryId"
            element={
              <ProtectedRoute>
                <DeliveryDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Note: `/developers/docs` is registered as a public top-level
              route above (outside this Layout), so we don't repeat it
              here. The in-app sidebar link can still navigate to the
              same path — React Router will match the public route. */}
          <Route
            path="support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          {/* NOTE: no index route here. React Router ranks an index route
              inside this "/*" splat ABOVE the exact "/" landing route, so an
              index redirect would hijack the root URL (→ dashboard → login).
              Bare "/" must fall through to the LandingPage route above. */}
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
